import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Avatar } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CommentIcon from '@mui/icons-material/Comment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChecklistIcon from '@mui/icons-material/Checklist';
import TaskTimerButton from './TaskTimerButton';
import { getTaskType, taskTypeIcon } from '../utils/taskTypes';
import CategoryChip from './pm/CategoryChip';

function TaskCard({ task, isDragging = false, onClick, sortableDisabled = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
    // Bei aktiver Board-Suche zeigt die Spalte nur einen Ausschnitt; eine
    // Zielposition daraus wäre falsch, deshalb ist Ziehen dann deaktiviert.
  } = useSortable({ id: task.id, disabled: sortableDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDueDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { text: `${Math.abs(diffDays)}d overdue`, color: 'error' };
    } else if (diffDays === 0) {
      return { text: 'Today', color: 'warning' };
    } else if (diffDays <= 3) {
      return { text: `${diffDays}d left`, color: 'warning' };
    } else {
      return { text: date.toLocaleDateString(), color: 'default' };
    }
  };

  const dueDate = formatDueDate(task.due_date);

  const handleCardClick = (e) => {
    // Don't trigger if clicking on buttons or other interactive elements
    if (e.target.closest('button') || e.target.closest('.MuiIconButton-root')) {
      return;
    }

    // Only trigger onClick if we have a handler
    if (onClick) {
      onClick(task);
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        cursor: sortableDisabled ? 'pointer' : (isDragging ? 'grabbing' : 'grab'),
        '&:hover': {
          boxShadow: 3,
        },
        flexShrink: 0,
      }}
    >
      <CardContent
        sx={{ p: 2, '&:last-child': { pb: 2 } }}
        onClick={handleCardClick}
        {...listeners}
      >
        {/* Task Header: Name + Timer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
              <Box
                component="span"
                title={getTaskType(task.task_type).label}
                sx={{ display: 'inline-flex', mt: '2px', color: getTaskType(task.task_type).color }}
              >
                {taskTypeIcon(task.task_type, { sx: { fontSize: 18 } })}
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {task.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: task.task_id ? 'monospace' : 'inherit' }}>
                {task.task_id || `#${task.id}`}
              </Typography>
              {/* Priorität dezent als Punkt + Text (früher farbiger Seitenstrich) */}
              {task.priority && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: `${getPriorityColor(task.priority)}.main` }} />
                  <Typography variant="caption" color="text.secondary">
                    {{ high: 'Hoch', medium: 'Mittel', low: 'Niedrig' }[task.priority] || task.priority}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          {/* Timer Button - Only show for tasks "In Bearbeitung" */}
          {task.status && task.status.name === 'In Bearbeitung' && (
            <TaskTimerButton task={task} compact={true} />
          )}
        </Box>

        {/* Kategorie */}
        {task.category_info && (
          <Box sx={{ mb: 1 }}>
            <CategoryChip category={task.category_info} sx={{ height: 20, fontSize: '0.7rem' }} />
          </Box>
        )}

        {/* Tags */}
        {task.tags && task.tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {task.tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: tag.color,
                  color: 'white',
                }}
              />
            ))}
          </Box>
        )}

        {/* Bottom Row: Meta Info */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
          }}
        >
          {/* Left Side: Icons & Counts */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {task.comment_count > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CommentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {task.comment_count}
                </Typography>
              </Box>
            )}

            {task.attachment_count > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {task.attachment_count}
                </Typography>
              </Box>
            )}

            {task.subtask_count > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ChecklistIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {task.subtask_count}
                </Typography>
              </Box>
            )}

            {task.effort_estimate && (
              <Chip
                label={`${task.effort_estimate} pts`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {/* Right Side: Assignee & Due Date */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {dueDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: `${dueDate.color}.main` }} />
                <Typography variant="caption" color={`${dueDate.color}.main`}>
                  {dueDate.text}
                </Typography>
              </Box>
            )}

            {task.assignee && (
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: '0.7rem',
                  bgcolor: 'primary.main',
                }}
              >
                {task.assignee.username ? task.assignee.username.charAt(0).toUpperCase() : '?'}
              </Avatar>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default TaskCard;
